/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * RCSB PDB fetcher. Given a 4-character PDB ID, returns the direct download
 * URL on RCSB's CDN — `https://files.rcsb.org/download/{id}.pdb`. RCSB's
 * `files.rcsb.org` host serves PDB / mmCIF files with permissive CORS, so a
 * plain browser `fetch` works without a proxy.
 *
 * We don't fetch + parse here — instead we hand the URL back so the existing
 * `loadMolecule` pipeline (which already knows how to fetch a `.pdb` URL,
 * pick the PDBParser, and route the result through processProteinResult) does
 * the work. That keeps the new "From PDB ID..." path identical to the
 * existing bundled-receptor path, just with an external URL.
 *
 * A separate `verifyPdbId` helper does a HEAD probe so the UI can show a
 * clear "no such PDB entry" toast BEFORE swapping the receptor — preventing
 * the chamber from going briefly blank on a typo.
 */

import { loadPdbCache, savePdbCache } from './pdbCache.ts';

const RCSB_BASE = 'https://files.rcsb.org/download/';
const PDB_ID_PATTERN = /^[1-9][A-Za-z0-9]{3}$/;

export const isValidPdbId = (raw: string): boolean => PDB_ID_PATTERN.test(raw.trim());

export const normalizePdbId = (raw: string): string => raw.trim().toUpperCase();

/**
 * Build the RCSB download URL for a PDB ID. Caller is responsible for
 * checking `isValidPdbId` first — this just formats.
 */
export const pdbUrl = (pdbId: string): string => `${RCSB_BASE}${normalizePdbId(pdbId)}.pdb`;

/**
 * mmCIF download URL for a PDB ID. Large / modern entries (many cryo-EM maps,
 * anything over 62 chains or 99,999 atoms — e.g. 3J3Q, 6EK0) are NOT distributed
 * in the legacy fixed-column PDB format at all; only mmCIF exists. Those return
 * 404 for the `.pdb` URL even though the entry is perfectly valid, so we fall
 * back to `.cif` (the chem-lib CIFParser handles PDBx/mmCIF).
 */
export const cifUrl = (pdbId: string): string => `${RCSB_BASE}${normalizePdbId(pdbId)}.cif`;

/**
 * Recover the PDB ID from an RCSB download URL (either `.pdb` or `.cif`, with or
 * without a query string). Returns the normalized 4-char id, or null if the URL
 * isn't an RCSB structure download — lets callers with only a stored `url` (e.g.
 * a reloaded biomolecule) route back through the cache-first `fetchPdbText`.
 */
export const pdbIdFromUrl = (url: string): string | null => {
  const m = /\/([1-9][A-Za-z0-9]{3})\.(?:pdb|cif)(?:\?|$)/i.exec(url);
  return m ? normalizePdbId(m[1]) : null;
};

// Above this download size we refuse an mmCIF fallback: parsing + building a
// cartoon for a multi-million-atom assembly (a whole ribosome / viral capsid)
// locks up the browser. ~30 MB of mmCIF is already a very large structure.
const MAX_CIF_BYTES = 30 * 1024 * 1024;

// A stalled RCSB connection would otherwise spin the loader forever. Cap the
// whole fetch (PDB probe + any mmCIF fallback + streaming) at this budget.
const FETCH_TIMEOUT_MS = 20000;

// Wrap a fetch operation in an abort timeout that also honors an externally
// supplied signal (e.g. an agent run's cancellation). `signal` is passed to
// fetch(); `timedOut()` distinguishes a timeout from a user/agent cancel so the
// caller can report the right reason; `done()` clears the timer + listener and
// MUST be called at every exit path. One budget covers the PDB attempt and the
// mmCIF fallback together.
type TimedFetch = { signal: AbortSignal; timedOut: () => boolean; done: () => void };
const startTimedFetch = (external?: AbortSignal): TimedFetch => {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, FETCH_TIMEOUT_MS);
  const onExternalAbort = () => controller.abort();
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', onExternalAbort, { once: true });
  }
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    done: () => {
      clearTimeout(timer);
      external?.removeEventListener('abort', onExternalAbort);
    },
  };
};

/**
 * RCSB renders a structure image for every entry and serves it on its CDN
 * (lowercase id, assembly-1 JPEG). We use it as a gallery-card thumbnail
 * fallback for fetched structures that were never opened in the lab (so no local
 * snapshot exists). A wrong/absent image just 404s — callers guard with an
 * <img onError> that drops back to the text placeholder — so it's always safe.
 */
export const rcsbImageUrl = (pdbId: string): string =>
  `https://cdn.rcsb.org/images/structures/${normalizePdbId(pdbId).toLowerCase()}_assembly-1.jpeg`;

/**
 * Fetch the PDB file body and validate that it actually contains atom records.
 * HEAD alone is too lenient — RCSB occasionally returns a 200 with non-PDB
 * content (an HTML stub page, or a structure that only exists in CIF), which
 * passes the HEAD check but fails downstream with "data does not contain valid
 * atoms" from PDBParser. Doing a real GET + content sniff here surfaces the
 * problem before we set projectState.protein and blank the chamber.
 *
 * Returns the PDB text on success so the caller can pass it via `data` to
 * `loadMolecule` (which has format detection in its data path), avoiding the
 * double-fetch a HEAD+GET pattern would incur.
 */
export type FetchPdbResult =
  // `format` tells the loader which parser to use; `url` is the source we
  // actually fetched (`.pdb` or `.cif`) so the structure records the right one.
  | { ok: true; text: string; format: 'pdb' | 'cif'; url: string }
  | { ok: false; reason: 'invalid_id' | 'not_found' | 'no_atoms' | 'network' | 'too_large' | 'timeout' };

// mmCIF fallback for entries with no legacy PDB file. RCSB serves mmCIF through
// CloudFront with NO Content-Length header, so a HEAD size probe is useless —
// instead we STREAM the body and abort the moment it exceeds MAX_CIF_BYTES, so a
// multi-gigabyte capsid never fully downloads and locks up the tab. Then sniff
// for atom_site records (the same HTML-stub guard as the PDB path). Cached under
// a `.cif`-suffixed key so it never collides with PDB text.
const fetchCifText = async (id: string, timed: TimedFetch): Promise<FetchPdbResult> => {
  const cacheKey = `${id}.cif`;
  const cached = loadPdbCache(cacheKey);
  if (cached) return { ok: true, text: cached, format: 'cif', url: cifUrl(id) };
  try {
    const response = await fetch(cifUrl(id), { signal: timed.signal });
    if (response.status === 404) return { ok: false, reason: 'not_found' };
    if (!response.ok) return { ok: false, reason: 'network' };
    const reader = response.body?.getReader();
    let text: string;
    if (reader) {
      const decoder = new TextDecoder();
      let total = 0;
      text = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        total += value.byteLength;
        if (total > MAX_CIF_BYTES) {
          await reader.cancel();
          return { ok: false, reason: 'too_large' };
        }
        text += decoder.decode(value, { stream: true });
      }
      text += decoder.decode();
    } else {
      // No streaming support — fall back to a buffered read + post-size check.
      text = await response.text();
      if (text.length > MAX_CIF_BYTES) return { ok: false, reason: 'too_large' };
    }
    if (!/_atom_site\./.test(text)) return { ok: false, reason: 'no_atoms' };
    savePdbCache(cacheKey, text);
    return { ok: true, text, format: 'cif', url: cifUrl(id) };
  } catch {
    return { ok: false, reason: timed.timedOut() ? 'timeout' : 'network' };
  }
};

export const fetchPdbText = async (pdbId: string, signal?: AbortSignal): Promise<FetchPdbResult> => {
  if (!isValidPdbId(pdbId)) return { ok: false, reason: 'invalid_id' };
  const id = normalizePdbId(pdbId);
  // Cross-project cache (localStorage, LRU). Spares the RCSB round-trip when
  // the user has fetched this ID before in any project on this origin.
  const cached = loadPdbCache(id);
  if (cached) return { ok: true, text: cached, format: 'pdb', url: pdbUrl(id) };

  const timed = startTimedFetch(signal);
  try {
    let response: Response;
    try {
      response = await fetch(pdbUrl(id), { signal: timed.signal });
    } catch {
      return { ok: false, reason: timed.timedOut() ? 'timeout' : 'network' };
    }
    // No legacy PDB file (too large / mmCIF-only) → try the mmCIF fallback before
    // giving up, so valid-but-large entries don't look like "no such structure".
    if (response.status === 404) return await fetchCifText(id, timed);
    if (!response.ok) return { ok: false, reason: 'network' };
    let text: string;
    try {
      text = await response.text();
    } catch {
      return { ok: false, reason: timed.timedOut() ? 'timeout' : 'network' };
    }
    // Sniff content: a real PDB file has ATOM or HETATM records on a line by
    // themselves. RCSB's error pages and HTML stubs do not.
    if (!/^(ATOM|HETATM)\s/m.test(text)) return { ok: false, reason: 'no_atoms' };
    // Cache the validated text for next time (cross-project).
    savePdbCache(id, text);
    return { ok: true, text, format: 'pdb', url: pdbUrl(id) };
  } finally {
    timed.done();
  }
};
